import React from "react";
import {
  PlusOutlined,
  DownloadOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";

import TableHeaderActions from "./TableHeaderActions";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";

/**
 * Role table component
 * Implements Single Responsibility Principle by focusing only on role data display
 */
const RoleTable = ({
  rolesData,
  selectedRowKeys,
  setSelectedRowKeys,
  searchValue,
  isFetching,
  onAddRole,
  onImportRoles,
  onViewRole,
  onEditRole,
  onDeleteRole,
  onAssignUsers,
  onManagePermissions,
  tabs,
  activeTab,
  onTabChange,
  onBulkDelete,
  onBulkEdit,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  paginationInfo = {},
}) => {
  // Define columns for roles data
  const rolesColumns = [
    {
      title: "Role Name",
      dataIndex: "name",
      key: "name",
      width: 250, // Increased width for role name column
      render: (text) => (
        <span className="font-semibold text-gray-900">{text}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 400,
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "No of Permissions",
      dataIndex: "permissions",
      key: "permissions",
      width: 400,
      render: (permissions) => (
        <span className="text-gray-600">{permissions?.length || 0}</span>
      ),
    },
  ];

  // Action buttons for role table
  const actionButtons = [
    {
      label: "Add Role",
      type: "primary",
      icon: <PlusOutlined />,
      onClick: onAddRole,
      className: "bg-orange-500 border-orange-500 hover:bg-orange-600",
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    onSelectAll: (selected, selectedRows, changeRows) => {
      console.log("Select all:", selected, selectedRows, changeRows);
    },
  };

  // Header actions when rows are selected
  const headerActions = [
    {
      label: "Bulk Edit",
      icon: <EditOutlined />,
      type: "primary",
      onClick: () => onBulkEdit && onBulkEdit(selectedRowKeys),
      disabled: selectedRowKeys.length === 0,
    },
    {
      label: "Bulk Delete",
      icon: <DeleteOutlined />,
      type: "default",
      danger: true,
      onClick: () => onBulkDelete && onBulkDelete(selectedRowKeys),
      disabled: selectedRowKeys.length === 0,
    },
  ];

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  // Row actions for role table
  const rowActions = [
    {
      label: "Assign Users",
      icon: <PlusCircleOutlined />,
      onClick: (record) => onAssignUsers(record),
    },
    {
      label: "View",
      icon: <EyeOutlined />,
      onClick: (record) => onViewRole(record),
    },
    {
      label: "Edit",
      icon: <EditOutlined />,
      onClick: (record) => onEditRole(record),
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: (record) => onDeleteRole(record),
    },
  ];

  return (
    <div>
      {/* Header actions when rows are selected */}
      <TableHeaderActions
        selectedCount={selectedRowKeys.length}
        totalCount={paginationInfo.total || rolesData.length}
        actions={headerActions}
        onClearSelection={handleClearSelection}
        p
      />

      <CustomDataTable
        title="Role Management"
        data={rolesData}
        columns={rolesColumns}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        showSearch={true}
        searchPlaceholder="Search roles..."
        searchValue={searchValue}
        searchMode="server"
        onServerSearch={onSearchChange}
        actionButtons={actionButtons}
        showFilter={false}
        showCSV={true}
        onFilterClick={() => console.log("Filter clicked")}
        onCSVClick={() => console.log("CSV clicked")}
        rowSelection={rowSelection}
        selectedRowKeys={selectedRowKeys}
        rowActions={rowActions}
        footerInfo={{
          left: `${selectedRowKeys.length} of ${rolesData.length} row(s) selected.`,
        }}
        loading={isFetching}
        pagination={true}
        pageSizeOptions={["10", "20", "50", "100"]}
        paginationMode="server"
        currentPage={paginationInfo.current_page || 1}
        totalItems={paginationInfo.total || 0}
        serverPageSize={paginationInfo.per_page || 10}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export default RoleTable;
